import { PersistedWorkflow } from '@cotera/sdk/core';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useWorkflowModel } from '../workflow.model';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

/**
 * The dataset this workflow is fired by, when it is fired by one.
 *
 * A node writing back to the same dataset that triggered the run is the
 * commonest shape there is — enrich the row that changed, put the answer next
 * to it — so the builder can answer questions the user would otherwise have to
 * answer twice: which dataset, and which columns identify a row in it.
 *
 * Reads the enabled binding, falling back to the first, the same way the
 * upstream-sample resolver picks which trigger a node's inputs come from; a
 * different choice here would suggest fields from a trigger the run never uses.
 */
export const useWorkflowDatasetTrigger = (): {
  readonly datasetId: string;
} | null => {
  const model = useWorkflowModel();
  const graph = useWatchableValue(model.graphWatchable);
  if (graph === null || graph === undefined) {
    return null;
  }
  const binding =
    graph.triggerBindings.find((row) => row.enabled === true) ??
    graph.triggerBindings[0];
  if (binding === undefined) {
    return null;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  if (
    snapshot === undefined ||
    snapshot.kind !== PersistedWorkflowTriggerKind.Dataset
  ) {
    return null;
  }
  const { datasetId } = snapshot.trigger;
  return datasetId === '' ? null : { datasetId };
};
